import { useNavigate } from 'react-router-dom';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Chip,
  Box,
  Typography,
  Divider,
} from '@mui/material';
import { CheckCircle, Pause, PlayArrow } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Session } from '@/types/session';
import { formatDate, formatTime } from '@utils/date';

interface SessionListProps {
  sessions: Session[];
  activeSessionId?: string | null;
  onSessionClick?: (session: Session) => void;
  // Optional selection mode props
  selectable?: boolean;
  selectedIds?: Set<string>;
  onSelectionToggle?: (sessionId: string) => void;
  onSelectAll?: () => void;
  onDeselectAll?: () => void;
  allSelected?: boolean;
  someSelected?: boolean;
}

export function SessionList({
  sessions,
  activeSessionId,
  onSessionClick,
  selectable,
  selectedIds,
  onSelectionToggle,
  onSelectAll,
  onDeselectAll,
  allSelected,
  someSelected,
}: SessionListProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const getStatusIcon = (status: Session['status']) => {
    if (status === 'completed') return <CheckCircle color="success" />;
    if (status === 'paused') return <Pause color="warning" />;
    return <PlayArrow color="primary" />;
  };

  const getStatusLabel = (status: Session['status']) => {
    if (status === 'completed') return t('sessionList.completed');
    if (status === 'paused') return t('sessionList.paused');
    return t('sessionList.inProgress');
  };

  const handleSessionClick = (session: Session) => {
    if (onSessionClick) {
      onSessionClick(session);
    } else {
      if (session.status === 'completed') {
        navigate(`/session/${session.sessionId}/review`);
      } else {
        navigate(`/session/${session.sessionId}/observe`);
      }
    }
  };

  if (sessions.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 300,
          gap: 2,
        }}
      >
        <Typography variant="h6" color="text.secondary">
          {t('sessionList.noSessions')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('sessionList.createNew')}
        </Typography>
      </Box>
    );
  }

  return (
    <List>
      {/* Select All header row */}
      {selectable && (
        <>
          <ListItem
            className="no-print"
            secondaryAction={
              <Typography variant="body2" color="text.secondary">
                {allSelected
                  ? t('sessionActions.deselectAll')
                  : t('sessionActions.selectAll')}
              </Typography>
            }
          >
            <ListItemIcon>
              <Checkbox
                edge="start"
                checked={allSelected}
                indeterminate={someSelected}
                onChange={() => {
                  if (allSelected) {
                    onDeselectAll?.();
                  } else {
                    onSelectAll?.();
                  }
                }}
              />
            </ListItemIcon>
          </ListItem>
          <Divider className="no-print" />
        </>
      )}

      {sessions.map((session) => {
        const isCurrentlyActive = activeSessionId === session.sessionId;
        const isSelected = selectable && selectedIds?.has(session.sessionId);

        return (
          <ListItem
            key={session.sessionId}
            disablePadding
            secondaryAction={undefined}
          >
            {selectable && (
              <ListItemIcon
                className="no-print"
                sx={{ minWidth: 42 }}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectionToggle?.(session.sessionId);
                }}
              >
                <Checkbox
                  edge="start"
                  checked={!!isSelected}
                  tabIndex={-1}
                />
              </ListItemIcon>
            )}
            <ListItemButton
              onClick={() => handleSessionClick(session)}
              sx={{
                ...(isCurrentlyActive
                  ? {
                      bgcolor: 'primary.50',
                      borderLeft: 3,
                      borderColor: 'primary.main',
                    }
                  : undefined),
                ...(isSelected
                  ? {
                      bgcolor: 'action.selected',
                    }
                  : undefined),
              }}
            >
              <ListItemText
                primary={
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    {getStatusIcon(session.status)}
                    <Typography variant="subtitle1" fontWeight={600}>
                      {session.wardName} - {t('sessionList.session')} #
                      {session.sessionNumber}
                    </Typography>
                  </Box>
                }
                secondary={
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(session.sessionDate)} at{' '}
                      {formatTime(session.startTime)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {session.facilityName} - {session.serviceName}
                    </Typography>
                    <Box
                      sx={{
                        mt: 1,
                        display: 'flex',
                        gap: 1,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Chip
                        label={
                          isCurrentlyActive
                            ? t('common.active')
                            : getStatusLabel(session.status)
                        }
                        size="small"
                        color={
                          isCurrentlyActive
                            ? 'primary'
                            : session.status === 'completed'
                              ? 'success'
                              : session.status === 'paused'
                                ? 'warning'
                                : 'default'
                        }
                        variant={isCurrentlyActive ? 'filled' : 'outlined'}
                      />
                    </Box>
                  </Box>
                }
              />
            </ListItemButton>
          </ListItem>
        );
      })}
    </List>
  );
}
